/** Storage key: safe internal path to land on after auth (e.g. `?next=` from middleware). */
export const POST_LOGIN_REDIRECT_KEY = "cardinal.postLoginRedirect"

/**
 * Use localStorage (not sessionStorage): magic links and OAuth often open in a new tab,
 * which does not share sessionStorage with the /login tab where ?next= was first seen.
 */
function storageGet(key: string): string | null {
    if (typeof window === "undefined") return null
    try {
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

function storageSet(key: string, value: string): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.setItem(key, value)
    } catch {
        /* ignore */
    }
}

function storageRemove(key: string): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.removeItem(key)
    } catch {
        /* ignore */
    }
}

/**
 * Accepts only same-site path+search strings to avoid open redirects.
 * Rejects protocol-relative and absolute URLs.
 */
export function sanitizePostLoginNext(raw: string | null | undefined): string | null {
    if (raw == null || typeof raw !== "string") return null
    let path = raw.trim()
    if (!path) return null
    if (path.includes("://")) {
        try {
            const u = new URL(path)
            path = u.pathname + u.search
        } catch {
            return null
        }
    }
    if (!path.startsWith("/") || path.startsWith("//")) return null
    if (path.startsWith("/api")) return null
    if (path.includes("\\") || path.includes("<")) return null
    return path
}

export function stashPostLoginRedirectFromQuery(nextParam: string | null): void {
    if (typeof window === "undefined") return
    const safe = sanitizePostLoginNext(nextParam != null ? decodeURIComponent(nextParam) : null)
    if (!safe) return
    storageSet(POST_LOGIN_REDIRECT_KEY, safe)
}

/** Read and remove stashed redirect (single use). */
export function consumePostLoginRedirect(): string | null {
    if (typeof window === "undefined") return null
    let v = storageGet(POST_LOGIN_REDIRECT_KEY)
    if (!v) {
        try {
            v = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
            if (v) window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
        } catch {
            /* ignore */
        }
    }
    if (!v) return null
    storageRemove(POST_LOGIN_REDIRECT_KEY)
    return sanitizePostLoginNext(v)
}

/** Stytch magic link / OAuth callback base is `/authenticate`; include `?next=` when present for new-tab flows. */
export function authenticateCallbackUrl(origin: string, nextQueryParam: string | null): string {
    const base = `${origin.replace(/\/$/, "")}/authenticate`
    const safe = sanitizePostLoginNext(nextQueryParam)
    if (!safe) return base
    return `${base}?next=${encodeURIComponent(safe)}`
}
